import { useLanguage } from '../../../hooks/useLanguage';
import { useProduct } from '../../../hooks/useProduct';
import { X, Instagram } from 'lucide-react';
import { useState } from 'react';
import emailjs from '@emailjs/browser';

const contactPhoto = `${import.meta.env.VITE_CLOUDFRONT_URL}/KOST-홈페이지-이미지_Contact.png`;

const content = {
  en: {
    title: 'CONTACT US',
    intro:
      "A question? A suggestion? An irresistible urge to tell us about your Korean Street experience? We're here!",
    messageTitle: '📧 Drop Us a Message',
    topics: [
      'Product questions: Ingredients, allergens, preparation tips - our Seoul experts answer everything',
      "Where to find us: Your favorite store doesn't have us yet? We'll take care of it!",
      'Feedback: Too good, not spicy enough, new flavor idea? We want to know everything',
    ],
    social: 'Our accounts:',
    form: {
      name: 'Your name',
      email: 'Email address',
      message: 'Tell us...',
      send: 'Send',
      sending: 'Sending...',
      success: 'Message sent successfully!',
      error: 'Failed to send message. Please try again.',
    },
  },
  fr: {
    title: 'CONTACTEZ-NOUS',
    intro:
      'Une question ? Une suggestion ? Une envie irrésistible de nous parler de votre expérience Korean Street ? On est là !',
    messageTitle: '📧 Laissez nous un message',
    topics: [
      'Questions produits : Ingrédients, allergènes, conseils de préparation - nos experts Seoul répondent à tout',
      "Où nous trouver : Votre magasin préféré ne nous a pas encore ? On s'en occupe !",
      'Feedback : Trop bon, pas assez épicé, idée de nouvelle saveur ? On veut tout savoir',
    ],
    social: 'Nos comptes :',
    form: {
      name: 'Votre nom',
      email: 'Adresse électronique',
      message: 'Dites-nous...',
      send: 'Envoyer',
      sending: 'Envoi en cours...',
      success: 'Message envoyé avec succès !',
      error: "Échec de l'envoi du message. Veuillez réessayer.",
    },
  },
};

export default function Contact() {
  const { language } = useLanguage();
  const { setActiveNavPanel } = useProduct();
  const data = content[language];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        {
          title: 'New Contact Form KOST',
          name: formData.name,
          email: formData.email,
          message: formData.message,
          to_email: 'info@louetlang.com',
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
      );

      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-full bg-yellow-400 relative">
      {/* Close Button - Mobile Only */}
      <button
        onClick={() => setActiveNavPanel(null)}
        className="lg:hidden absolute top-4 right-4 z-50"
      >
        <X className="w-8 h-8 text-white" />
      </button>

      {/* Title */}
      <div className="px-4 md:px-6 pt-6 md:pt-8 pb-4">
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-tight">
          {language === 'en' ? 'CONTACT' : 'CONTACTEZ'}
        </h1>
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-tight">
          {language === 'en' ? 'US' : 'NOUS'}
        </h1>
      </div>

      {/* Header Image */}
      <div className="w-full px-4 md:px-6 py-4 md:py-6">
        <div className="relative w-full aspect-square md:aspect-auto overflow-hidden rounded-2xl">
          <img
            src={contactPhoto}
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Intro Section */}
      <div className="px-4 md:px-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-lg border-4 border-yellow-500">
          <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
            {data.intro}
          </p>
          <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
            {data.messageTitle}
          </h3>
          <ul className="space-y-2">
            {data.topics.map((topic, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-gray-600 mt-1">•</span>
                <span className="text-gray-700">{topic}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Social Media Links */}
      <div className="px-4 md:px-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-lg border-4 border-yellow-500">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            {data.social}
          </h3>
          <div className="space-y-3">
            <a
              href="https://www.instagram.com/koreanstreet.official/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-red-600 hover:text-red-700 transition-colors"
            >
              <Instagram className="w-5 h-5" />
              <span className="font-semibold">Instagram</span>
            </a>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="px-4 md:px-6 pb-20 lg:pb-8">
        <div className="bg-white rounded-xl p-6 shadow-lg border-4 border-yellow-500">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Formulaire</h3>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={data.form.name}
              required
              className="w-full px-4 py-3 bg-yellow-400 text-gray-800 placeholder-gray-700 font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={data.form.email}
              required
              className="w-full px-4 py-3 bg-yellow-400 text-gray-800 placeholder-gray-700 font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={data.form.message}
              rows={6}
              required
              className="w-full px-4 py-3 bg-yellow-400 text-gray-800 placeholder-gray-700 font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
            />
            {status === 'success' && (
              <div className="p-3 bg-green-500 text-white rounded-lg text-center font-medium">
                {data.form.success}
              </div>
            )}
            {status === 'error' && (
              <div className="p-3 bg-red-500 text-white rounded-lg text-center font-medium">
                {data.form.error}
              </div>
            )}
            {status !== 'success' && (
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-colors"
              >
                {status === 'sending' ? data.form.sending : data.form.send}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
